import {
  useController,
  FieldValues,
  UseControllerProps,
} from "react-hook-form";
import React from "react";
import "./styles/controlled-select.css";

export interface SelectOption {
  value: string | number;
  label: string;
  disabled?: boolean;
}

// Generic ControlledSelect for use with react-hook-form
export type ControlledSelectProps<T extends FieldValues> = Omit<
  UseControllerProps<T>,
  "control"
> &
  Omit<
    React.ComponentProps<"select">,
    "name" | "value" | "onChange" | "ref"
  > & {
    control: UseControllerProps<T>["control"];
    label?: string;
    helperText?: string;
    placeholder?: string;
    options: SelectOption[];
    startAdornment?: React.ReactNode;
    endAdornment?: React.ReactNode;
  };

const Select = React.forwardRef<
  HTMLSelectElement,
  React.ComponentProps<"select">
>(({ className, children, ...props }, ref) => {
  return (
    <select
      className={`form-input form-select controlled-select__select ${
        className || ""
      }`}
      ref={ref}
      {...props}
    >
      {children}
    </select>
  );
});
Select.displayName = "Select";

export function ControlledSelect<T extends FieldValues>({
  control,
  name,
  defaultValue,
  rules,
  shouldUnregister,
  label,
  helperText,
  placeholder,
  options,
  startAdornment,
  endAdornment,
  ...selectProps
}: ControlledSelectProps<T>) {
  const {
    field,
    fieldState: { error },
  } = useController<T>({
    control: control!,
    name,
    ...(defaultValue !== undefined && { defaultValue }),
    ...(rules !== undefined && { rules }),
    ...(shouldUnregister !== undefined && { shouldUnregister }),
  });

  const hasStart = Boolean(startAdornment);
  const hasEnd = Boolean(endAdornment);

  const selectClassName = [
    hasStart ? "form-input--with-start" : "",
    hasEnd ? "form-input--with-end" : "",
    selectProps.className ?? "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div
      className={`form-field controlled-select ${
        error ? "form-field--error" : ""
      }`}
    >
      {label && (
        <span
          className={`form-field__label ${
            selectProps.required ? "form-field__label--required" : ""
          }`}
        >
          {label}
        </span>
      )}

      <div className="form-field__container controlled-select__container">
        {hasStart && (
          <div className="form-adornment form-adornment--start controlled-select__adornment controlled-select__adornment--start">
            {startAdornment}
          </div>
        )}

        <Select {...selectProps} {...field} className={selectClassName}>
          {placeholder && (
            <option value="" disabled>
              {placeholder}
            </option>
          )}
          {options.map((option) => (
            <option
              key={option.value}
              value={option.value}
              disabled={option.disabled}
            >
              {option.label}
            </option>
          ))}
        </Select>

        {hasEnd && (
          <div className="form-adornment form-adornment--end controlled-select__adornment controlled-select__adornment--end">
            {endAdornment}
          </div>
        )}
      </div>

      {helperText && <span className="form-field__helper">{helperText}</span>}
      {error && <span className="form-field__error">{error.message}</span>}
    </div>
  );
}
